import { FC } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './ActivityInfo.module.scss';
import { useAppSelector } from '../../../../../../../hooks/redux';
import { TripStop } from '../../../../../../../types/trip.types';
import { ActivityField, getActivityFields } from '../../config/activityFieldsConfig';

interface ActivityInfoProps {
  activity: TripStop;
}

type FieldSegment =
  | { kind: 'group'; groupId: string; fields: ActivityField[] }
  | { kind: 'row'; field: ActivityField; index: number };

const buildFieldSegments = (fields: ActivityField[]): FieldSegment[] => {
  const segments: FieldSegment[] = [];
  let i = 0;
  while (i < fields.length) {
    const f = fields[i];
    const gid = f.fieldGroupId;
    if (gid) {
      const chunk: ActivityField[] = [f];
      let j = i + 1;
      while (j < fields.length && fields[j].fieldGroupId === gid) {
        chunk.push(fields[j]);
        j++;
      }
      segments.push({ kind: 'group', groupId: gid, fields: chunk });
      i = j;
    } else {
      segments.push({ kind: 'row', field: f, index: i });
      i++;
    }
  }
  return segments;
};

const ActivityInfo: FC<ActivityInfoProps> = ({ activity }) => {
  const user = useAppSelector(state => state.user);
  const fields = getActivityFields(activity.stop_type);

  const formatFieldValue = (field: ActivityField, activity: TripStop): string => {
    const value = field.getValue(activity);
    if (!value || value.trim() === '') return '';

    try {
      switch (field.type) {
        case 'dateTime':
          return formatDateTime(value);
        case 'date':
          return formatDate(value);
        case 'time':
          return formatTime(value);
        case 'address':
          return value;
        case 'airport':
          return value;
        case 'country':
          return value;
        default:
          return value;
      }
    } catch (error) {
      console.warn(`Error formatting field ${field.key}:`, error);
      return value;
    }
  };

  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      return (
        date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }) +
        ', ' +
        date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      );
    } catch (error) {
      console.warn(`Error formatting datetime ${dateString}:`, error);
      return dateString;
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (error) {
      console.warn(`Error formatting date ${dateString}:`, error);
      return dateString;
    }
  };

  const formatTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.warn(`Error formatting time ${dateString}:`, error);
      return dateString;
    }
  };

  const fieldsWithValues = fields.filter(field => {
    try {
      const value = field.getValue(activity);
      return value && value.trim() !== '';
    } catch (error) {
      console.warn(`Error getting value for field ${field.key}:`, error);
      return false;
    }
  });

  const allFields = [...fieldsWithValues];

  if (allFields.length === 0) {
    return (
      <Box className={styles.activityInfo}>
        <Box className={styles.infoRow}>
          <Typography
            variant="h10"
            className={styles.noDataText}
          >
            No additional details available
          </Typography>
        </Box>
      </Box>
    );
  }

  const hasDateRangeStartDivider = (field: ActivityField) => {
    const pairedDateFields: Record<string, string> = {
      departure: 'arrival',
      checkIn: 'checkOut',
      startDate: 'endDate',
      pickupDate: 'dropoffDate',
      date: 'endDate',
    };

    const endFieldKey = pairedDateFields[field.key];
    if (!endFieldKey) return false;

    return allFields.some(candidate => candidate.key === endFieldKey);
  };

  const hasDateRangeEndDivider = (field: ActivityField) => {
    const pairedDateFields: Record<string, string> = {
      arrival: 'departure',
      checkOut: 'checkIn',
      endDate: 'startDate',
      dropoffDate: 'pickupDate',
    };

    const startFieldKey = pairedDateFields[field.key];
    if (!startFieldKey) return false;

    return allFields.some(candidate => candidate.key === startFieldKey);
  };

  const hasNotesBelow = Boolean(activity.notes?.trim());
  const hasBudgetBelow = (activity.amount_spend ?? 0) > 0 && Boolean(user.show_trip_budgets);
  const hasBorderedFooterBelow = hasBudgetBelow || hasNotesBelow;

  const shouldShowDateRangeEndBottomBorder = (field: ActivityField) => {
    if (allFields.length === 0) return false;
    const lastKey = allFields[allFields.length - 1].key;
    if (field.key !== lastKey) return false;

    return hasDateRangeEndDivider(field) && !hasNotesBelow && !hasBudgetBelow;
  };

  const nextVisibleFieldHasTopDivider = (index: number) => {
    const next = allFields[index + 1];
    if (!next) return false;
    return Boolean(next.sectionDividerBefore) || hasDateRangeStartDivider(next);
  };

  const nextFieldStartsGroup = (index: number) => {
    const next = allFields[index + 1];
    return Boolean(next?.fieldGroupId);
  };

  const renderFieldRow = (
    field: ActivityField,
    insideGroup: boolean,
    indexInAll: number,
    suppressTopAfterGroup?: boolean
  ) => {
    try {
      const formattedValue = formatFieldValue(field, activity);

      if (!formattedValue || formattedValue.trim() === '') return null;

      const isLastVisibleField = indexInAll >= 0 && indexInAll === allFields.length - 1;

      const showTopDivider =
        !insideGroup &&
        !suppressTopAfterGroup &&
        (Boolean(field.sectionDividerBefore) || hasDateRangeStartDivider(field));

      const showBottomFromConfig =
        !insideGroup && Boolean(field.sectionDividerAfter) && !nextVisibleFieldHasTopDivider(indexInAll);

      const wouldShowBottom = showBottomFromConfig || shouldShowDateRangeEndBottomBorder(field);

      const showBottomDivider =
        !insideGroup &&
        !(hasBorderedFooterBelow && isLastVisibleField) &&
        wouldShowBottom &&
        !(nextFieldStartsGroup(indexInAll) && wouldShowBottom);

      return (
        <Box
          key={field.key}
          className={`${styles.infoRow} ${showTopDivider ? styles.sectionDividerTopRow : ''} ${
            showBottomDivider ? styles.sectionDividerBottomRow : ''
          }`}
        >
          <Typography
            variant="h10"
            className={styles.label}
            sx={{ color: '#222B3A' }}
          >
            {field.label}
          </Typography>
          <Typography
            variant="h10Bold"
            className={styles.value}
            sx={{ color: '#222B3A' }}
          >
            {formattedValue}
          </Typography>
        </Box>
      );
    } catch (error) {
      console.warn(`Error rendering field ${field.key}:`, error);
      return null;
    }
  };

  const segments = buildFieldSegments(allFields);

  return (
    <Box className={styles.activityInfo}>
      {segments.map((seg, segIdx) => {
        const isLastSegment = segIdx === segments.length - 1;
        const prevSeg = segIdx > 0 ? segments[segIdx - 1] : null;
        const flushGroupBottom = isLastSegment && seg.kind === 'group' && hasBorderedFooterBelow;
        const groupStackAfterGroup = seg.kind === 'group' && prevSeg?.kind === 'group';

        if (seg.kind === 'group') {
          return (
            <Box
              key={`${seg.groupId}-${segIdx}`}
              className={`${styles.fieldGroup} ${groupStackAfterGroup ? styles.fieldGroupNoTop : ''} ${
                flushGroupBottom ? styles.fieldGroupFlushFooter : ''
              }`}
            >
              {seg.fields.map(field => renderFieldRow(field, true, -1))}
            </Box>
          );
        }

        const suppressTopAfterGroup = prevSeg?.kind === 'group';

        return renderFieldRow(seg.field, false, seg.index, suppressTopAfterGroup);
      })}
    </Box>
  );
};

export default ActivityInfo;
